import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { getItem, setItem, removeItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const NEIGHBOURS_KEY = 'NEIGHBOURS';

interface NeighboursData {
  searchUrl: string;
  nextUrl?: string | null;
  prevUrl?: string | null;
  paths: string[];
  index: number;
}

const getSearchResults = async (data: NeighboursData, url: string, next: boolean, callback: () => void) => {
  const result = await axios<PropertySearchResult>(
    {
      url: `${API_URL}/search-results?url=${encodeURIComponent(url)}`,
    },
    undefined,
  );

  if (result.data.results.length) {
    const paths = result.data.results.map((i) => i.path);

    if (next) {
      setItem(NEIGHBOURS_KEY, {
        ...data,
        searchUrl: result.data.url,
        paths: [...data.paths, ...paths],
        nextUrl: result.data.nextUrl,
      });
    } else {
      setItem(NEIGHBOURS_KEY, {
        ...data,
        searchUrl: result.data.url,
        paths: [...paths, ...data.paths],
        prevUrl: result.data.prevUrl,
      });
    }

    callback();
  }
};

export const setNeighboursData = (result: PropertySearchResult, property: Property) => {
  const data: NeighboursData = {
    searchUrl: result.url,
    paths: result.results.map((item) => item.path),
    nextUrl: result.nextUrl,
    prevUrl: result.prevUrl,
    index: result.results.indexOf(property),
  };

  setItem(NEIGHBOURS_KEY, data);
};

export const clearNeighbours = () => {
  removeItem(NEIGHBOURS_KEY);
};

export const useNeighbours = (path?: string) => {
  const [nextLink, setNextLink] = useState<string>('');
  const [prevLink, setPrevLink] = useState<string>('');
  const [searchUrl, setSearchUrl] = useState<string>('');
  const router = useRouter();

  const loadLinks = () => {
    const data = getItem<NeighboursData>(NEIGHBOURS_KEY);

    if (data && path) {
      const validIndex = path === data.paths[data.index] && data.index > -1;

      if (validIndex) {
        if (data.index > 0) setPrevLink(data.paths[data.index - 1]);
        else if (data.prevUrl) getSearchResults(data, data.prevUrl, false, loadLinks);

        if (data.index + 1 < data.paths.length) setNextLink(data.paths[data.index + 1]);
        else if (data.nextUrl) getSearchResults(data, data.nextUrl, true, loadLinks);
      }

      setSearchUrl(data.searchUrl);
    }
  };

  useEffect(() => {
    loadLinks();

    const handleHistoryChange = (url: string) => {
      const data = getItem<NeighboursData>(NEIGHBOURS_KEY);

      if (data) {
        // Check if prev link clicked
        if (data.index > 0 && data.paths[data.index - 1] === url) {
          setItem(NEIGHBOURS_KEY, { ...data, index: data.index - 1 });
        }
        // Check if next link clicked
        else if (data.index + 1 < data.paths.length && data.paths[data.index + 1] === url) {
          setItem(NEIGHBOURS_KEY, { ...data, index: data.index + 1 });
        }
      }
    };

    router.events.on('beforeHistoryChange', handleHistoryChange);

    return () => {
      router.events.off('beforeHistoryChange', handleHistoryChange);
    };
  }, []);

  return { nextLink, prevLink, searchUrl };
};

export default useNeighbours;
